/**
 * Medication Order Entry Step Component
 * 
 * Second step in the MedPass workflow - handles selection of medications,
 * dosing instructions, and basic safety checks.
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Form,
    Input,
    InputNumber,
    Select,
    Button,
    Alert,
    Divider,
    Space,
    Table,
    Modal,
    message,
    Tooltip,
    Badge,
    Popconfirm,
    AutoComplete,
    Tag
} from 'antd';
import {
    MedicineBoxOutlined,
    PlusOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    SearchOutlined,
    SafetyCertificateOutlined,
    EditOutlined
} from '@ant-design/icons';

import medicationService from '../../../services/medicationService';
import medpassService from '../../../services/medpassService';

const { Option } = Select;
const { TextArea } = Input;

const MedicationOrderEntryStep = ({ workflow, onSave, onNext, onPrev, loading }) => {
    const [form] = Form.useForm();
    const [medications, setMedications] = useState([]);
    const [showMedicationSearch, setShowMedicationSearch] = useState(false);
    const [medicationSearchResults, setMedicationSearchResults] = useState([]);
    const [medicationSearchLoading, setMedicationSearchLoading] = useState(false);
    const [editingMedication, setEditingMedication] = useState(null);
    const [safetyChecks, setSafetyChecks] = useState({
        drugInteractions: false,
        allergies: false,
        contraindications: false
    });

    // Route of administration options
    const routeOptions = [
        { value: 'oral', label: 'Oral' },
        { value: 'topical', label: 'Topical' },
        { value: 'intravenous', label: 'Intravenous (IV)' },
        { value: 'intramuscular', label: 'Intramuscular (IM)' },
        { value: 'subcutaneous', label: 'Subcutaneous (SC)' },
        { value: 'inhalation', label: 'Inhalation' },
        { value: 'sublingual', label: 'Sublingual' },
        { value: 'rectal', label: 'Rectal' },
        { value: 'ophthalmic', label: 'Ophthalmic' },
        { value: 'otic', label: 'Otic' },
        { value: 'nasal', label: 'Nasal' },
        { value: 'transdermal', label: 'Transdermal' }
    ];

    // Dosage form options
    const dosageFormOptions = [
        { value: 'tablet', label: 'Tablet' },
        { value: 'capsule', label: 'Capsule' },
        { value: 'liquid', label: 'Liquid' },
        { value: 'syrup', label: 'Syrup' },
        { value: 'injection', label: 'Injection' },
        { value: 'cream', label: 'Cream' },
        { value: 'ointment', label: 'Ointment' },
        { value: 'drops', label: 'Drops' },
        { value: 'inhaler', label: 'Inhaler' },
        { value: 'patch', label: 'Patch' },
        { value: 'suppository', label: 'Suppository' },
        { value: 'powder', label: 'Powder' }
    ];

    // Helper function to format route names
    const formatRouteName = (route) => {
        const routeLabels = {
            'oral': 'Oral',
            'topical': 'Topical',
            'intravenous': 'Intravenous (IV)',
            'intramuscular': 'Intramuscular (IM)',
            'subcutaneous': 'Subcutaneous (SC)',
            'inhalation': 'Inhalation',
            'sublingual': 'Sublingual',
            'rectal': 'Rectal',
            'ophthalmic': 'Ophthalmic',
            'otic': 'Otic',
            'nasal': 'Nasal',
            'transdermal': 'Transdermal'
        };
        return routeLabels[route] || route;
    };

    // Initialize with existing medications
    useEffect(() => {
        if (workflow?.medications) {
            setMedications(workflow.medications);
            
            // Update safety check status
            setSafetyChecks({
                drugInteractions: workflow.drug_interactions_checked || false,
                allergies: workflow.allergies_checked || false,
                contraindications: workflow.contraindications_checked || false
            });
        }
    }, [workflow]);

    // Search medications
    const searchMedications = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 3) {
            setMedicationSearchResults([]);
            return;
        }

        setMedicationSearchLoading(true);
        try {
            const result = await medicationService.searchMedications({
                query: searchTerm,
                limit: 20
            });

            if (result.success) {
                setMedicationSearchResults(result.data);
            }
        } catch (error) {
            console.error('Error searching medications:', error);
            message.error('Failed to search medications');
        } finally {
            setMedicationSearchLoading(false);
        }
    };

    // Add medication from search
    const addMedication = (medicationData) => {
        // Determine route from DoH data or default based on dosage form
        let defaultRoute = 'oral';
        
        if (medicationData.route_of_administration && medicationData.route_of_administration.length > 0) {
            // Use the first route from DoH data
            defaultRoute = medicationData.route_of_administration[0];
        } else {
            // Fallback: determine route based on dosage form
            const formToRouteMap = {
                'tablet': 'oral',
                'capsule': 'oral',
                'liquid': 'oral',
                'syrup': 'oral',
                'injection': 'intramuscular',
                'cream': 'topical',
                'ointment': 'topical',
                'drops': 'ophthalmic',
                'inhaler': 'inhalation',
                'patch': 'transdermal',
                'suppository': 'rectal',
                'powder': 'oral'
            };
            defaultRoute = formToRouteMap[medicationData.dosage_form] || 'oral';
        }

        const newMedication = {
            medication_id: medicationData.medication_id,
            drug_code: medicationData.drug_code,
            brand_name: medicationData.brand_name,
            generic_name: medicationData.generic_name,
            strength: medicationData.strengths?.[0]?.value + medicationData.strengths?.[0]?.unit || '',
            dosage_form: medicationData.dosage_form,
            route: defaultRoute,
            dose_amount: '1',
            frequency: 'once daily', // Default frequency - will be updated when form is filled
            duration: '7 days',
            directions: 'Take as directed',
            quantity: 30,
            days_supply: 30,
            refills_allowed: 0,
            refills_remaining: 0,
            indication: '',
            special_instructions: '',
            generic_substitution_allowed: true,
            brand_medically_necessary: false,
            status: 'active',
            dispensed_quantity: 0,
            last_dispensed_date: null
        };

        setMedications([...medications, newMedication]);
        setShowMedicationSearch(false);
        message.success('Medication added successfully');
    };

    // Edit medication
    const editMedication = (index) => {
        const medication = medications[index];
        setEditingMedication({ ...medication, index });
        
        form.setFieldsValue({
            dose_amount: medication.dose_amount,
            frequency: medication.frequency,
            route: medication.route,
            duration: medication.duration,
            directions: medication.directions,
            quantity: medication.quantity,
            days_supply: medication.days_supply,
            refills_allowed: medication.refills_allowed,
            indication: medication.indication,
            special_instructions: medication.special_instructions,
            generic_substitution_allowed: medication.generic_substitution_allowed,
            brand_medically_necessary: medication.brand_medically_necessary
        });
    };

    // Save medication edits
    const saveMedicationEdit = async () => {
        try {
            const values = await form.validateFields();
            const updatedMedications = [...medications];
            updatedMedications[editingMedication.index] = {
                ...editingMedication,
                ...values
            };
            
            setMedications(updatedMedications);
            setEditingMedication(null);
            form.resetFields();
            message.success('Medication updated successfully');
        } catch (error) {
            message.error('Please complete all required fields');
        }
    };

    // Remove medication
    const removeMedication = (index) => {
        const updatedMedications = medications.filter((_, i) => i !== index);
        setMedications(updatedMedications);
        message.success('Medication removed');
    };

    // Run safety checks
    const runSafetyChecks = async () => {
        if (!workflow?.workflow_id) {
            message.error('Please save the medications first');
            return;
        }

        try {
            const result = await medpassService.runSafetyChecks(workflow.workflow_id);
            if (result.success) {
                const alerts = result.data;
                
                setSafetyChecks({
                    drugInteractions: true,
                    allergies: true,
                    contraindications: true
                });

                if (alerts.length > 0) {
                    const criticalAlerts = alerts.filter(alert => alert.requires_action);
                    
                    Modal.warning({
                        title: 'Safety Alerts Found',
                        content: (
                            <div>
                                <p>{alerts.length} safety alerts found ({criticalAlerts.length} require action)</p>
                                <ul>
                                    {alerts.slice(0, 5).map((alert, index) => (
                                        <li key={index}>
                                            <Badge 
                                                status={alert.requires_action ? 'error' : 'warning'} 
                                                text={alert.message} 
                                            />
                                        </li>
                                    ))}
                                </ul>
                                {alerts.length > 5 && <p>... and {alerts.length - 5} more</p>}
                            </div>
                        ),
                        width: 600
                    });
                } else {
                    message.success('No safety issues found');
                }
            }
        } catch (error) {
            message.error('Failed to run safety checks');
        }
    };

    // Table columns for medications
    const medicationColumns = [
        {
            title: 'Medication',
            key: 'medication',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{record.brand_name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.generic_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.strength} {record.dosage_form}
                    </div>
                </div>
            )
        },
        {
            title: 'Dosing',
            key: 'dosing',
            render: (_, record) => (
                <div>
                    <div>{record.dose_amount} {record.frequency}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Duration: {record.duration}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Route: <Tag color="blue">{formatRouteName(record.route)}</Tag>
                    </div>
                </div>
            )
        },
        {
            title: 'Quantity',
            key: 'quantity',
            render: (_, record) => (
                <div>
                    <div>Qty: {record.quantity}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.days_supply} days supply
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Refills: {record.refills_allowed}
                    </div>
                </div>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record, index) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<EditOutlined />}
                        onClick={() => editMedication(index)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Remove medication?"
                        onConfirm={() => removeMedication(index)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />}
                        >
                            Remove
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    // Save step data
    const handleSave = async () => {
        if (medications.length === 0) {
            message.error('Please add at least one medication');
            return false;
        }

        const validation = medpassService.validateMedicationEntry(medications);
        if (!validation.isValid) {
            message.error(validation.errors[0]);
            return false;
        }

        const updateData = {
            medications,
            drug_interactions_checked: safetyChecks.drugInteractions,
            allergies_checked: safetyChecks.allergies,
            contraindications_checked: safetyChecks.contraindications
        };

        const success = await onSave(updateData, 'medications');
        if (success) {
            message.success('Medication orders saved successfully');
            return true;
        }
        return false;
    };

    const handleNext = async () => {
        const success = await handleSave();
        if (success && onNext) {
            // Add a small delay to ensure workflow state is updated
            await new Promise(resolve => setTimeout(resolve, 100));
            await onNext();
        }
    };

    return (
        <div className="medication-order-entry-step">
            <Card 
                title={
                    <Space>
                        <MedicineBoxOutlined />
                        Medication Order Entry
                    </Space>
                }
                extra={
                    <Space>
                        <Button
                            type="primary"
                            ghost
                            icon={<SafetyCertificateOutlined />}
                            onClick={runSafetyChecks}
                            disabled={medications.length === 0}
                        >
                            Run Safety Checks
                        </Button>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={() => setShowMedicationSearch(true)}
                        >
                            Add Medication
                        </Button>
                    </Space>
                }
            >
                {/* Safety Status */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Alert
                            message="Safety Checks Status"
                            description={
                                <Space>
                                    <Badge 
                                        status={safetyChecks.drugInteractions ? 'success' : 'error'}
                                        text="Drug Interactions" 
                                    />
                                    <Badge 
                                        status={safetyChecks.allergies ? 'success' : 'error'}
                                        text="Allergies" 
                                    />
                                    <Badge 
                                        status={safetyChecks.contraindications ? 'success' : 'error'}
                                        text="Contraindications" 
                                    />
                                </Space>
                            }
                            type={
                                safetyChecks.drugInteractions && safetyChecks.allergies && safetyChecks.contraindications
                                    ? 'success'
                                    : 'warning'
                            }
                            showIcon
                        />
                    </Col>
                </Row>

                {/* Medications Table */}
                <Table
                    dataSource={medications}
                    columns={medicationColumns}
                    rowKey={(record, index) => index}
                    pagination={false}
                    locale={{
                        emptyText: (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <MedicineBoxOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                                <div style={{ marginTop: 16, color: '#999' }}>
                                    No medications added yet
                                </div>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={() => setShowMedicationSearch(true)}
                                    style={{ marginTop: 16 }}
                                >
                                    Add First Medication
                                </Button>
                            </div>
                        )
                    }}
                />

                {/* Patient Information Alert */}
                {workflow?.patient_profile && (
                    <Alert
                        message="Patient Information"
                        description={
                            <div>
                                <div><strong>Allergies:</strong> {
                                    workflow.patient_profile.allergies?.length > 0 
                                        ? workflow.patient_profile.allergies.join(', ')
                                        : 'None documented'
                                }</div>
                                <div><strong>Current Medications:</strong> {
                                    workflow.patient_profile.current_medications?.length > 0
                                        ? workflow.patient_profile.current_medications.join(', ')
                                        : 'None documented'
                                }</div>
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                )}

                {/* Action Buttons */}
                <Row justify="space-between" style={{ marginTop: 24 }}>
                    <Col>
                        <Button onClick={onPrev}>
                            Previous
                        </Button>
                    </Col>
                    
                    <Col>
                        <Button type="primary" onClick={handleNext} loading={loading}>
                            Save & Next Step
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Medication Search Modal */}
            <Modal
                title="Search and Add Medication"
                open={showMedicationSearch}
                onCancel={() => setShowMedicationSearch(false)}
                footer={null}
                width={1000}
            >
                <Input.Search
                    placeholder="Search by medication name, generic name, or drug code"
                    onSearch={searchMedications}
                    loading={medicationSearchLoading}
                    style={{ marginBottom: 16 }}
                />
                
                <Table
                    dataSource={medicationSearchResults}
                    rowKey="medication_id"
                    pagination={false}
                    size="small"
                    scroll={{ y: 400 }}
                    columns={[
                        {
                            title: 'Medication',
                            key: 'medication',
                            render: (_, record) => (
                                <div>
                                    <div style={{ fontWeight: 'bold' }}>{record.brand_name}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {record.generic_name}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {record.therapeutic_class}
                                    </div>
                                </div>
                            )
                        },
                        {
                            title: 'Strength',
                            dataIndex: 'strengths',
                            render: (strengths) => 
                                strengths?.map(s => `${s.value}${s.unit}`).join(', ') || 'N/A'
                        },
                        {
                            title: 'Form',
                            dataIndex: 'dosage_form',
                            render: (form) => form?.charAt(0).toUpperCase() + form?.slice(1) || 'N/A'
                        },
                        {
                            title: 'Route (DoH)',
                            key: 'route',
                            render: (_, record) => {
                                const routes = record.route_of_administration;
                                if (routes && routes.length > 0) {
                                    return (
                                        <div>
                                            {routes.map((route, index) => (
                                                <Tag key={index} color="green" size="small">
                                                    {formatRouteName(route)}
                                                </Tag>
                                            ))}
                                        </div>
                                    );
                                }
                                return <span style={{ color: '#999' }}>Not specified</span>;
                            }
                        },
                        {
                            title: 'Action',
                            key: 'action',
                            render: (_, record) => (
                                <Button 
                                    type="primary" 
                                    size="small"
                                    onClick={() => addMedication(record)}
                                >
                                    Add
                                </Button>
                            )
                        }
                    ]}
                />
                
                {medicationSearchResults.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        No medications found. Try a different search term.
                    </div>
                )}
            </Modal>

            {/* Medication Edit Modal */}
            <Modal
                title="Edit Medication"
                open={!!editingMedication}
                onOk={saveMedicationEdit}
                onCancel={() => {
                    setEditingMedication(null);
                    form.resetFields();
                }}
                width={800}
            >
                {editingMedication && (
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <Alert
                            message={`Editing: ${editingMedication.brand_name} (${editingMedication.generic_name})`}
                            type="info"
                            style={{ marginBottom: 16 }}
                        />
                        
                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Dose Amount"
                                    name="dose_amount"
                                    rules={[{ required: true, message: 'Dose amount required' }]}
                                >
                                    <Input placeholder="e.g., 1 tablet, 5ml" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Frequency"
                                    name="frequency"
                                    rules={[{ required: true, message: 'Frequency required' }]}
                                >
                                    <Select placeholder="Select frequency">
                                        <Option value="once daily">Once daily</Option>
                                        <Option value="twice daily">Twice daily</Option>
                                        <Option value="three times daily">Three times daily</Option>
                                        <Option value="four times daily">Four times daily</Option>
                                        <Option value="every 4 hours">Every 4 hours</Option>
                                        <Option value="every 6 hours">Every 6 hours</Option>
                                        <Option value="every 8 hours">Every 8 hours</Option>
                                        <Option value="every 12 hours">Every 12 hours</Option>
                                        <Option value="as needed">As needed</Option>
                                        <Option value="with meals">With meals</Option>
                                        <Option value="at bedtime">At bedtime</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Duration"
                                    name="duration"
                                    rules={[{ required: true, message: 'Duration required' }]}
                                >
                                    <Input placeholder="e.g., 7 days, 2 weeks" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Route of Administration"
                                    name="route"
                                    rules={[{ required: true, message: 'Route required' }]}
                                >
                                    <Select placeholder="Select route">
                                        {routeOptions.map(option => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Quantity"
                                    name="quantity"
                                    rules={[{ required: true, message: 'Quantity required' }]}
                                >
                                    <InputNumber min={1} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Days Supply"
                                    name="days_supply"
                                    rules={[{ required: true, message: 'Days supply required' }]}
                                >
                                    <InputNumber min={1} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Refills Allowed"
                                    name="refills_allowed"
                                >
                                    <InputNumber min={0} max={11} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24}>
                                <Form.Item
                                    label="Directions for Use"
                                    name="directions"
                                    rules={[{ required: true, message: 'Directions required' }]}
                                >
                                    <TextArea 
                                        placeholder="Enter detailed directions for patient"
                                        rows={3}
                                    />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Indication"
                                    name="indication"
                                >
                                    <Input placeholder="Reason for medication" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Special Instructions"
                                    name="special_instructions"
                                >
                                    <TextArea placeholder="Any special instructions" rows={2} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default MedicationOrderEntryStep;